import React, { useEffect, lazy, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation
} from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';

// Always-needed: loaded eagerly
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import CookieConsent from './components/CookieConsent';
import MobileAppBanner from './components/MobileAppBanner';
import { trackPageView } from './utils/analytics';
import { captureReferralFromURL } from './utils/referralUtils';
import { trackRedditPageView } from './utils/redditPixel';

// Route-level code splitting — each chunk loads only when the route is visited
const Landing         = lazy(() => import('./components/Landing'));
const Dashboard       = lazy(() => import('./components/Dashboard'));
const Offers          = lazy(() => import('./components/Offers'));
const OfferDetail     = lazy(() => import('./components/OfferDetails'));
const Earnings        = lazy(() => import('./components/Earnings'));
const Profile         = lazy(() => import('./components/Profile'));
const Payments        = lazy(() => import('./components/payments'));

const BlogList        = lazy(() => import('./components/Bloglist'));
const BlogPost        = lazy(() => import('./components/blogpost'));
const CashbackOffers  = lazy(() => import('./components/CashbackOffers'));
const EarnMoneyGaming = lazy(() => import('./components/EarnMoneyGaming'));
const MobileGameRewards = lazy(() => import('./components/MobileGameRewards'));

// Admin — heaviest chunk, only admins ever load these
const AdminDashboard  = lazy(() => import('./components/admin/AdminDashboard'));
const AdminUsers      = lazy(() => import('./components/admin/AdminUsers'));
const AdminOffers     = lazy(() => import('./components/admin/AdminOffers'));
const AdminSurveyCreate = lazy(() => import('./components/admin/AdminSurveyCreate'));
const AdminSurveyList = lazy(() => import('./components/admin/AdminSurveyList'));
const AdminSocialOfferCreate = lazy(() => import('./components/admin/AdminSocialOfferCreate'));
const AdminSocialOfferList = lazy(() => import('./components/admin/AdminSocialOfferList'));
const AdminCallbacks  = lazy(() => import('./components/admin/admincallbacks'));
const AdminBlogs      = lazy(() => import('./components/admin/adminblogs'));
const AdminPayouts    = lazy(() => import('./components/admin/adminpayouts'));
const AdminAnalytics  = lazy(() => import('./components/admin/AdminAnalytics'));
const AdminAdSpend    = lazy(() => import('./components/admin/AdminAdSpend'));
const AdminCohorts    = lazy(() => import('./components/admin/AdminCohorts'));
const AdminGoogleAgent = lazy(() => import('./components/admin/AdminGoogleAgent'));
const AdminGraphs     = lazy(() => import('./components/admin/AdminGraphs'));
const AdminAppAnalytics = lazy(() => import('./components/admin/AdminAppAnalytics'));
const AdminAppVersion = lazy(() => import('./components/admin/AdminAppVersion'));
const AdminAIAnalyst  = lazy(() => import('./components/admin/AdminAIAnalyst'));

const PrivacyPolicy   = lazy(() => import('./components/legal/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./components/legal/TermsConditions'));
const CookiePolicy    = lazy(() => import('./components/legal/CookiePolicy'));
const ContactUs       = lazy(() => import('./components/legal/ContactUs'));

// Component to track page changes
// Component to track page changes
const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Get user-friendly page title based on route
    const getPageTitle = (pathname) => {
      const routes = {
        '/': 'Landing Page',
        '/login': 'Login Page',
        '/signup': 'Sign Up Page',
        '/dashboard': 'Dashboard',
        '/offers': 'Offers Page',
        '/payments': 'Payments Page',
        '/profile': 'Profile Page',
        '/blog': 'Blog',
        '/admin': 'Admin Login',
        '/cashback-offers': 'Cashback Offers Landing',
        '/earn-money-gaming': 'Earn Money Gaming Landing',
        '/mobile-game-rewards': 'Mobile Game Rewards Landing',
      };

      // Handle dynamic routes like /offers/:id
      if (pathname.startsWith('/offers/') && pathname !== '/offers') {
        return 'Offer Details Page';
      }
      if (pathname.startsWith('/blog/')) {
        return 'Blog Post';
      }

      return routes[pathname] || pathname;
    };

    const pageTitle = getPageTitle(location.pathname);
    
    // Track page view with clean title
    trackPageView(location.pathname + location.search, pageTitle);

     trackRedditPageView();
    
    console.log('📊 Page view tracked:', pageTitle, location.pathname);
  }, [location]);

  return null;
};


const App = () => {

/*     useEffect(() => {
    // Initialize Google Analytics on app start
    initGA();
    initRedditPixel();
  }, []); */

    // ✅ NEW: Capture referral code on app load
  useEffect(() => {
    // Check URL for referral code when app loads
    captureReferralFromURL();
  }, []);

  return (
    <AuthProvider>
      <Router>
        <div className="app-shell">
          <AnalyticsTracker />
          <Suspense fallback={null}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Login isSignUp={true} />} />
            <Route path="/admin" element={<Login isAdminLogin={true} />} />
            <Route path="/cashback-offers" element={<CashbackOffers />} />
            <Route path="/earn-money-gaming" element={<EarnMoneyGaming />} />
            <Route path="/mobile-game-rewards" element={<MobileGameRewards />} />
            
            {/* Blog Routes - Public Access with Public Navigation */}   
            <Route path="/blog" element={<BlogList />} />     
            <Route path="/blog/:slug" element={<BlogPost />} />
          
            
            {/* Legal Pages - Public Access */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-conditions" element={<TermsConditions />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/contact" element={<ContactUs />} />
            
            {/* Protected User Routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
            path="/offers" 
            element={<Offers />}
             />
  
            <Route 
            path="/offers/:offerId" 
            element={<OfferDetail />} 
            />

            <Route 
              path="/earnings" 
              element={
                <ProtectedRoute>
                  <Earnings />
                </ProtectedRoute>
              } 
            />
            <Route path="/payments" 
            element={<ProtectedRoute>
              <Payments />
              </ProtectedRoute>
            } />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile/:section" 
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } 
            />

            {/* Protected Admin Routes */}
            <Route 
              path="/admin/dashboard" 
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/users" 
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminUsers />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/admin/offers"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminOffers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/surveys"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminSurveyList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/surveys/new"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminSurveyCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/surveys/:id/edit"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminSurveyCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/social-offers"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminSocialOfferList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/social-offers/new"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminSocialOfferCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/social-offers/:id/edit"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminSocialOfferCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/payouts"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminPayouts />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/callbacks" 
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminCallbacks />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/blogs" 
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminBlogs />
                </ProtectedRoute>
              } 
            />
            <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute adminOnly={true}>
                <AdminAnalytics />
              </ProtectedRoute>
            }
          />
            <Route
            path="/admin/ad-spend"
            element={
              <ProtectedRoute adminOnly={true}>
                <AdminAdSpend />
              </ProtectedRoute>
            }
          />
            <Route
            path="/admin/google-agent"
            element={
              <ProtectedRoute adminOnly={true}>
                <AdminGoogleAgent />
              </ProtectedRoute>
            }
          />
            <Route
            path="/admin/cohorts"
            element={
              <ProtectedRoute adminOnly={true}>
                <AdminCohorts />
              </ProtectedRoute>
            }
          />
            <Route
            path="/admin/app-analytics"
            element={
              <ProtectedRoute adminOnly={true}>
                <AdminAppAnalytics />
              </ProtectedRoute>
            }
          />
            <Route
            path="/admin/graphs"
            element={
              <ProtectedRoute adminOnly={true}>
                <AdminGraphs />
              </ProtectedRoute>
            }
          />
            <Route
            path="/admin/app-version"
            element={
              <ProtectedRoute adminOnly={true}>
                <AdminAppVersion />
              </ProtectedRoute>
            }
          />
            <Route
            path="/admin/ai-analyst"
            element={
              <ProtectedRoute adminOnly={true}>
                <AdminAIAnalyst />
              </ProtectedRoute>
            }
          />

            {/* Fallback route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>

          {/* Global Components */}
          <CookieConsent />
          <MobileAppBanner />
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App;